import React, { useEffect, useRef } from 'react';
import {
  FilterForm,
  SelectInput,
  TextInput,
  useGetList,
  useListContext,
} from 'react-admin';
import { Stack, Box } from '@mui/material';

import type { FacetGroup } from '@src/facets';
import type { ConditionOption } from '@src/conditions';

import type { PricingType } from './types';

/**
 * Primary Name filter that depends on the selected Primary Tag filter.
 * Shows values from the selected tag's facet group.
 */
const PrimaryNameFilterInput = ({ alwaysOn }: { alwaysOn?: boolean }) => {
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');
  const { filterValues, setFilters, displayedFilters } = useListContext();

  const selectedTag = filterValues?.tag as string | undefined;

  // Refs so the effect can read current values without triggering it to re-run.
  // filterValues is a new object reference every render — putting it in the
  // useEffect dependency array below would cause the effect to run on every
  // filter change, not just when selectedTag changes.
  const filterValuesRef = useRef(filterValues);
  filterValuesRef.current = filterValues;
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;
  const displayedFiltersRef = useRef(displayedFilters);
  displayedFiltersRef.current = displayedFilters;

  // Clear name filter when tag is removed. PrimaryTagFilterInput.onChange handles
  // the case where tag changes to another value, but the resettable × button in
  // FilterForm context can bypass that onChange and leave a stale name in filterValues.
  useEffect(() => {
    if (!selectedTag && filterValuesRef.current.name) {
      setFiltersRef.current(
        { ...filterValuesRef.current, name: undefined },
        displayedFiltersRef.current,
      );
    }
  }, [selectedTag]);

  const tagFacet = facetGroups.find(fg => fg.tag === selectedTag);
  const nameChoices = tagFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  return (
    <SelectInput
      source="name"
      label="Primary Name"
      choices={nameChoices}
      disabled={!selectedTag}
      resettable
      helperText={false}
      alwaysOn={alwaysOn}
      sx={{ width: '150px' }}
      onChange={(e) => {
        const newFilters = { ...filterValues, name: e?.target?.value || undefined };
        setFilters(newFilters, displayedFilters);
      }}
    />
  );
};

/**
 * Primary Tag filter that clears the Primary Name filter when changed.
 */
const PrimaryTagFilterInput = ({ choices, alwaysOn }: { choices: { id: string, name: string }[], alwaysOn?: boolean }) => {
  const { filterValues, setFilters, displayedFilters } = useListContext();

  return (
    <SelectInput
      source="tag"
      label="Primary Tag"
      choices={choices}
      resettable
      helperText={false}
      alwaysOn={alwaysOn}
      sx={{ width: '150px' }}
      onChange={(e) => {
        // Clear name when tag changes
        const newFilters = { ...filterValues, tag: e?.target?.value || undefined, name: undefined };
        setFilters(newFilters, displayedFilters);
      }}
    />
  );
};

interface ConditionDiscountsToolbarProps {
  pricingType: PricingType;
}

const ConditionDiscountsToolbar = ({ pricingType }: ConditionDiscountsToolbarProps) => {
  const isConditionType = pricingType === 'condition';

  // Both types use facets (department, category, tag, name filters).
  // Conditions are only needed for the condition type.
  const { data: facetGroups = [], isLoading: facetsLoading } = useGetList<FacetGroup>('facets');
  const { data: conditions = [], isLoading: conditionsLoading } = useGetList<ConditionOption>('conditions', {}, { enabled: isConditionType });

  // Build choices for department and category filters
  const departmentFacet = facetGroups.find(fg => fg.tag === 'department');
  const categoryFacet = facetGroups.find(fg => fg.tag === 'category');

  const departmentChoices = departmentFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  const categoryChoices = categoryFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  // Build tag choices from all available facet tags
  const tagChoices = facetGroups.map(fg => ({
    id: fg.tag,
    name: fg.tag,
  }));

  const conditionChoices = conditions.map(c => ({ id: c.code, name: c.label }));

  const filters = isConditionType ? [
    <SelectInput
      key="condition"
      source="condition"
      label="Condition"
      choices={conditionChoices}
      disabled={conditionsLoading}
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '180px' }}
    />,
    <SelectInput
      key="department"
      source="department"
      label="Department"
      choices={departmentChoices}
      disabled={facetsLoading}
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <SelectInput
      key="category"
      source="category"
      label="Category"
      choices={categoryChoices}
      disabled={facetsLoading}
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <TextInput
      key="merchantCategory"
      source="merchantCategory"
      label="Merchant Category"
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <PrimaryTagFilterInput key="tag" choices={tagChoices} alwaysOn />,
    <PrimaryNameFilterInput key="name" alwaysOn />,
  ] : [
    <SelectInput
      key="department"
      source="department"
      label="Department"
      choices={departmentChoices}
      disabled={facetsLoading}
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <SelectInput
      key="category"
      source="category"
      label="Category"
      choices={categoryChoices}
      disabled={facetsLoading}
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <PrimaryTagFilterInput key="tag" choices={tagChoices} alwaysOn />,
    <PrimaryNameFilterInput key="name" alwaysOn />,
    <TextInput
      key="merchantCategory"
      source="merchantCategory"
      label="Merchant Category"
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <TextInput
      key="upc"
      source="upc"
      label="UPC"
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <FilterForm filters={filters} />
      </Stack>
    </Box>
  );
};

export default ConditionDiscountsToolbar;
